import { useEffect, useMemo, useState } from "react";
import { X, GitBranch, TrendingUp, TrendingDown, ArrowRight } from "lucide-react";
import { Area, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis, Line, ComposedChart } from "recharts";
import { api, formatApiError } from "@/lib/api";
import { formatMoney } from "@/lib/format";
import { TEST_IDS } from "@/testIds";
import { toast } from "sonner";

function typeColor(t) {
  return t === "new" ? "bg-gold-500" : t === "ongoing" ? "bg-ink" : t === "land" ? "bg-[#7BB3F0]" : "bg-muted-light";
}

export default function ScenarioDiffModal({ scenario, onClose }) {
  const versions = scenario?.versions || [];
  const defaultTo = versions.length ? versions[versions.length - 1].version_no : 0;
  const defaultFrom = versions.length > 1 ? versions[versions.length - 2].version_no : defaultTo;

  const [vFrom, setVFrom] = useState(defaultFrom);
  const [vTo, setVTo] = useState(defaultTo);
  const [diff, setDiff] = useState(null);
  const [loading, setLoading] = useState(false);

  // Close on Escape — matches PageShell / SaveModal behavior.
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    if (!scenario || vFrom === vTo) { setDiff(null); return; }
    setLoading(true);
    api.get(`/scenarios/${scenario.id}/diff`, { params: { v_from: vFrom, v_to: vTo } })
      .then(({ data }) => setDiff(data))
      .catch((e) => toast.error("Diff failed", { description: formatApiError(e.response?.data?.detail) }))
      .finally(() => setLoading(false));
  }, [scenario, vFrom, vTo]);

  const chartData = useMemo(
    () => (diff?.timeline_diff || []).map((t) => ({ label: t.label, from: t.from, to: t.to, delta: t.delta })),
    [diff]
  );
  const totalPositive = (diff?.total_delta ?? 0) >= 0;

  if (!scenario) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6" data-testid={TEST_IDS.scenarioDiffModal}>
      <div className="absolute inset-0 bg-ink/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-4xl max-h-full overflow-y-auto cf-card p-6 space-y-4">
        <div className="flex items-start justify-between">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted-light font-semibold">
              <GitBranch size={11} /> Version diff
            </div>
            <div className="font-display text-xl md:text-2xl text-ink mt-1 truncate" style={{ fontWeight: 800, letterSpacing: "-0.02em" }}>
              {scenario.name}
            </div>
          </div>
          <button
            data-testid={TEST_IDS.scenarioDiffClose}
            onClick={onClose}
            className="w-9 h-9 rounded-full hover:bg-surface-2 flex items-center justify-center text-muted-dark shrink-0"
          >
            <X size={16} />
          </button>
        </div>

        {/* Version pickers */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-muted-dark">Compare</span>
          <select
            data-testid={TEST_IDS.scenarioDiffFromSel}
            value={vFrom}
            onChange={(e) => setVFrom(parseInt(e.target.value, 10))}
            className="px-3 py-1.5 rounded-lg bg-paper text-sm text-ink focus:outline-none border border-hairline"
          >
            {versions.map((v) => <option key={v.version_no} value={v.version_no}>v{v.version_no} · {v.note || "(no note)"}</option>)}
          </select>
          <ArrowRight size={13} className="text-muted-light" />
          <select
            data-testid={TEST_IDS.scenarioDiffToSel}
            value={vTo}
            onChange={(e) => setVTo(parseInt(e.target.value, 10))}
            className="px-3 py-1.5 rounded-lg bg-paper text-sm text-ink focus:outline-none border border-hairline"
          >
            {versions.map((v) => <option key={v.version_no} value={v.version_no}>v{v.version_no} · {v.note || "(no note)"}</option>)}
          </select>
        </div>

        {loading && <div className="text-sm text-muted-light">Loading…</div>}

        {vFrom === vTo && (
          <div className="text-sm text-muted-dark cf-card p-4 bg-paper/50">
            Pick two different versions to see the diff.
          </div>
        )}

        {diff && vFrom !== vTo && (
          <>
            {/* Totals */}
            <div className="grid grid-cols-3 gap-3">
              <div className="cf-card p-4">
                <div className="text-[10px] uppercase tracking-widest text-muted-light font-semibold">v{diff.from.version_no} projected</div>
                <div className="num text-lg text-ink font-bold mt-1">{formatMoney(diff.from.projected_total)}</div>
                <div className="text-[10px] text-muted-dark mt-0.5 truncate">{diff.from.note || "(no note)"}</div>
              </div>
              <div className="cf-card p-4">
                <div className="text-[10px] uppercase tracking-widest text-muted-light font-semibold">v{diff.to.version_no} projected</div>
                <div className="num text-lg text-ink font-bold mt-1">{formatMoney(diff.to.projected_total)}</div>
                <div className="text-[10px] text-muted-dark mt-0.5 truncate">{diff.to.note || "(no note)"}</div>
              </div>
              <div
                className="cf-card p-4"
                style={{
                  background: totalPositive ? "linear-gradient(135deg,rgba(18,183,106,0.08),rgba(18,183,106,0.02))" : "linear-gradient(135deg,rgba(240,68,56,0.08),rgba(240,68,56,0.02))",
                }}
              >
                <div className="text-[10px] uppercase tracking-widest text-muted-light font-semibold">Total delta</div>
                <div
                  className="num text-lg font-bold mt-1 inline-flex items-center gap-1"
                  style={{ color: totalPositive ? "#0F7F41" : "#B72A20" }}
                >
                  {totalPositive ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                  {totalPositive ? "+" : ""}{formatMoney(diff.total_delta, { showZero: true })}
                </div>
              </div>
            </div>

            {/* Chart: v_from vs v_to per month */}
            <div className="cf-card p-4">
              <div className="text-[10px] uppercase tracking-widest text-muted-light font-semibold mb-2">
                Timeline · v{diff.from.version_no} vs v{diff.to.version_no}
              </div>
              <div style={{ height: 220 }}>
                <ResponsiveContainer width="100%" height={220} minWidth={200}>
                  <ComposedChart data={chartData}>
                    <defs>
                      <linearGradient id="dfFrom" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#8A8776" stopOpacity={0.35} />
                        <stop offset="100%" stopColor="#8A8776" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke="rgba(18,49,89,0.06)" vertical={false} />
                    <XAxis dataKey="label" tick={{ fill: "#8A8776", fontSize: 10 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fill: "#8A8776", fontSize: 10 }} axisLine={false} tickLine={false} />
                    <Tooltip
                      contentStyle={{ background: "#0B1F3B", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, color: "#fff", fontSize: 11 }}
                      formatter={(v, key) => [formatMoney(v), key === "from" ? `v${diff.from.version_no}` : key === "to" ? `v${diff.to.version_no}` : "Δ"]}
                    />
                    <Area type="monotone" dataKey="from" stroke="#8A8776" fill="url(#dfFrom)" strokeWidth={2} />
                    <Line type="monotone" dataKey="to" stroke="#FBC02D" strokeWidth={2.5} dot={false} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Project deltas */}
            <div className="cf-card p-4">
              <div className="text-[10px] uppercase tracking-widest text-muted-light font-semibold mb-2">
                Top project movers ({(diff.project_diff || []).length} projects)
              </div>
              <div className="max-h-56 overflow-y-auto">
                <table className="w-full">
                  <thead className="sticky top-0 bg-surface text-[10px] uppercase tracking-widest text-muted-light">
                    <tr>
                      <th className="text-left px-2 py-2">Project</th>
                      <th className="text-right px-2 py-2 num">v{diff.from.version_no}</th>
                      <th className="text-right px-2 py-2 num">v{diff.to.version_no}</th>
                      <th className="text-right px-2 py-2 num">Δ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(diff.project_diff || []).map((p) => {
                      const pos = p.delta >= 0;
                      const flat = Math.abs(p.delta) < 0.01;
                      return (
                        <tr key={p.project_name} className="border-b border-hairline/40 last:border-0">
                          <td className="px-2 py-1.5 text-sm text-ink">
                            <span className={`inline-block w-1.5 h-1.5 rounded-full ${typeColor(p.project_type)} mr-1.5 align-middle`} />
                            {p.project_name}
                          </td>
                          <td className="px-2 py-1.5 text-right num text-xs text-muted-dark">{formatMoney(p.from)}</td>
                          <td className="px-2 py-1.5 text-right num text-xs text-ink font-semibold">{formatMoney(p.to)}</td>
                          <td
                            className="px-2 py-1.5 text-right num text-xs font-semibold"
                            style={{ color: flat ? "#8A8776" : pos ? "#0F7F41" : "#B72A20" }}
                          >
                            {flat ? "–" : `${pos ? "+" : ""}${formatMoney(p.delta, { showZero: true })}`}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export function CompareLiveModal({ scenarioId, version, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    setLoading(true);
    api.get(`/scenarios/${scenarioId}/versions/${version.version_no}/compare-live`)
      .then(({ data }) => setData(data))
      .catch((e) => setError(formatApiError(e.response?.data?.detail) || "Compare failed"))
      .finally(() => setLoading(false));
  }, [scenarioId, version.version_no]);

  const chartData = useMemo(
    () => (data?.timeline_diff || []).map((t) => ({ label: t.label, frozen: t.frozen, live: t.live, drift: t.drift })),
    [data]
  );
  const positive = (data?.total_drift ?? 0) >= 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6" data-testid={TEST_IDS.scenarioCompareLiveModal}>
      <div className="absolute inset-0 bg-ink/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-3xl max-h-full overflow-y-auto cf-card p-6 space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-light font-semibold">Frozen snapshot vs live data</div>
            <div className="font-display text-xl text-ink mt-1" style={{ fontWeight: 800, letterSpacing: "-0.02em" }}>
              v{version.version_no} · projection drift
            </div>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full hover:bg-surface-2 flex items-center justify-center text-muted-dark">
            <X size={16} />
          </button>
        </div>

        {loading && <div className="text-sm text-muted-light">Loading…</div>}
        {error && <div className="text-sm text-red-600 cf-card p-3 bg-red-50 border-red-200">{error}</div>}

        {data && (
          <>
            <div className="grid grid-cols-3 gap-3">
              <div className="cf-card p-4">
                <div className="text-[10px] uppercase tracking-widest text-muted-light font-semibold">Frozen</div>
                <div className="num text-lg text-ink font-bold mt-1">{formatMoney(data.frozen_total)}</div>
              </div>
              <div className="cf-card p-4">
                <div className="text-[10px] uppercase tracking-widest text-muted-light font-semibold">Live now</div>
                <div className="num text-lg text-ink font-bold mt-1">{formatMoney(data.live_total)}</div>
              </div>
              <div
                className="cf-card p-4"
                style={{ background: positive ? "linear-gradient(135deg,rgba(18,183,106,0.08),rgba(18,183,106,0.02))" : "linear-gradient(135deg,rgba(240,68,56,0.08),rgba(240,68,56,0.02))" }}
              >
                <div className="text-[10px] uppercase tracking-widest text-muted-light font-semibold">Drift</div>
                <div className="num text-lg font-bold mt-1 inline-flex items-center gap-1" style={{ color: positive ? "#0F7F41" : "#B72A20" }}>
                  {positive ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                  {positive ? "+" : ""}{formatMoney(data.total_drift, { showZero: true })}
                </div>
              </div>
            </div>

            <div className="cf-card p-4">
              <div className="text-[10px] uppercase tracking-widest text-muted-light font-semibold mb-2">Timeline drift</div>
              <div style={{ height: 220 }}>
                <ResponsiveContainer width="100%" height={220} minWidth={200}>
                  <ComposedChart data={chartData}>
                    <defs>
                      <linearGradient id="frozenGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#7BB3F0" stopOpacity={0.4} />
                        <stop offset="100%" stopColor="#7BB3F0" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke="rgba(18,49,89,0.06)" vertical={false} />
                    <XAxis dataKey="label" tick={{ fill: "#8A8776", fontSize: 10 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fill: "#8A8776", fontSize: 10 }} axisLine={false} tickLine={false} />
                    <Tooltip
                      contentStyle={{ background: "#0B1F3B", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, color: "#fff", fontSize: 11 }}
                      formatter={(v, key) => [formatMoney(v), key === "frozen" ? "Frozen" : key === "live" ? "Live" : "Drift"]}
                    />
                    <Area type="monotone" dataKey="frozen" stroke="#7BB3F0" fill="url(#frozenGrad)" strokeWidth={2} />
                    <Line type="monotone" dataKey="live" stroke="#FBC02D" strokeWidth={2.5} dot={false} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-2 text-[11px] text-muted-dark leading-relaxed">
                <span className="text-[#3971AE] font-semibold">Blue area</span> = frozen at save time ·{" "}
                <span className="text-gold-600 font-semibold">gold line</span> = re-run against current data with the same levers.
                Divergence indicates underlying data changed since save.
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
